import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AttendanceService } from '../../services/attendance.service';
import { AttendanceReportDto, AttendanceSessionDto, EmployeeResponseDto, EmployeeAttendanceSummaryDto, DailyAttendanceRecordDto } from '../../interfaces/interfaces';

@Component({
  selector: 'app-attendance-report',
  templateUrl: './attendance-report.component.html',
  styleUrls: ['./attendance-report.component.scss']
})
export class AttendanceReportComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  expandedEmployee: EmployeeAttendanceSummaryDto | null = null;
  displayedColumns: string[] = ['employeePhoto', 'employeeName', 'totalDaysWorked', 'totalHoursWorked', 'expand'];
  dataSource = new MatTableDataSource<EmployeeAttendanceSummaryDto>();
  
  filterForm: FormGroup;
  loading = false;
  employees: EmployeeResponseDto[] = [];

  constructor(
    private attendanceService: AttendanceService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar
  ) {
    this.filterForm = this.fb.group({
      employeeId: [''],
      startDate: [''],
      endDate: ['']
    });
  }

  ngOnInit(): void {
    this.loadEmployees();
    this.setDefaultDates();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  setDefaultDates(): void {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.filterForm.patchValue({
      startDate: startOfMonth,
      endDate: endOfMonth
    });
  }

  loadEmployees(): void {
    this.attendanceService.getAllEmployees(true).subscribe({
      next: (response) => {
        this.employees = response.employees;
      },
      error: (error) => {
        console.error('Error loading employees:', error);
        this.snackBar.open('Error loading employees', 'Close', { duration: 3000 });
      }
    });
  }

  generateReport(): void {
    if (this.filterForm.valid) {
      this.loading = true;
      const formValue = this.filterForm.value;
      const startDate = formValue.startDate ? this.formatDate(formValue.startDate) : '';
      const endDate = formValue.endDate ? this.formatDate(formValue.endDate) : '';
      const employeeId = formValue.employeeId || undefined;

      this.attendanceService.getAttendanceReport(employeeId, startDate, endDate).subscribe({
        next: (response) => {
          // Convert the flat structure to employee-grouped structure
          const employeeSummaries = this.convertToEmployeeSummaries(response.attendanceReports);
          this.dataSource.data = employeeSummaries;
          this.loading = false;
          if (employeeSummaries.length === 0) {
            this.snackBar.open('No attendance records found for the selected criteria', 'Close', { duration: 3000 });
          }
        },
        error: (error) => {
          this.snackBar.open('Error loading attendance report', 'Close', { duration: 3000 });
          this.loading = false;
        }
      });
    }
  }

  convertToEmployeeSummaries(reports: AttendanceReportDto[]): EmployeeAttendanceSummaryDto[] {
    const employeeMap = new Map<number, EmployeeAttendanceSummaryDto>();

    for (const report of reports) {
      if (!employeeMap.has(report.employeeId)) {
        employeeMap.set(report.employeeId, {
          employeeId: report.employeeId,
          employeeName: report.employeeName,
          employeePhoto: report.employeePhoto,
          totalDaysWorked: 0,
          totalHoursWorked: 0,
          hasFaceVerificationIssues: false,
          dailyRecords: []
        });
      }

      const employeeSummary = employeeMap.get(report.employeeId)!;
      
      // Add daily record
      const dailyRecord: DailyAttendanceRecordDto = {
        date: typeof report.date === 'string' ? report.date : new Date(report.date).toISOString().split('T')[0],
        totalHours: report.totalHours,
        isComplete: report.isComplete,
        sessions: report.sessions,
        hasFaceVerificationIssues: report.hasFaceVerificationIssues
      };
      
      employeeSummary.dailyRecords.push(dailyRecord);
      
      // Update totals - count days only if there are sessions or hours worked
      if (report.sessions.length > 0 || report.totalHours > 0) {
        employeeSummary.totalDaysWorked++;
      }
      employeeSummary.totalHoursWorked += report.totalHours;
      if (report.hasFaceVerificationIssues) {
        employeeSummary.hasFaceVerificationIssues = true;
      }
    }

    // Sort daily records by date for each employee
    for (const employeeSummary of employeeMap.values()) {
      employeeSummary.dailyRecords.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }

    return Array.from(employeeMap.values());
  }

  formatDate(date: Date): string {
    // Use local date formatting to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatTime(timeString: string): string {
    if (!timeString) return '-';
    return new Date(timeString).toLocaleTimeString();
  }

  formatDateDisplay(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  getTotalHours(): number {
    return this.dataSource.data.reduce((total, employee) => total + employee.totalHoursWorked, 0);
  }

  getTotalDays(): number {
    return this.dataSource.data.reduce((total, employee) => total + employee.totalDaysWorked, 0);
  }

  getTotalEmployees(): number {
    return this.dataSource.data.length;
  }

  getEmployeesWithFaceIssues(): number {
    return this.dataSource.data.filter(employee => employee.hasFaceVerificationIssues).length;
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  exportToCSV(): void {
    const data = this.dataSource.data;
    if (data.length === 0) {
      this.snackBar.open('No data to export', 'Close', { duration: 3000 });
      return;
    }

    const headers = [
      'Employee Name',
      'Total Days Worked',
      'Total Hours Worked',
      'Date',
      'Session #',
      'Clock In',
      'Clock Out',
      'Session Hours',
      'Complete'
    ];

    const rows: string[][] = [];
    for (const employee of data) {
      for (const dailyRecord of employee.dailyRecords) {
        for (let i = 0; i < dailyRecord.sessions.length; i++) {
          const session = dailyRecord.sessions[i];
          rows.push([
            employee.employeeName,
            employee.totalDaysWorked.toString(),
            employee.totalHoursWorked.toFixed(2),
            this.formatDateDisplay(dailyRecord.date),
            (i + 1).toString(),
            session.clockInTime ? this.formatTime(session.clockInTime) : '-',
            session.clockOutTime ? this.formatTime(session.clockOutTime) : '-',
            session.durationHours.toFixed(2),
            dailyRecord.isComplete ? 'Yes' : 'No'
          ]);
        }
      }
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `attendance_report_${this.formatDate(new Date())}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    this.snackBar.open('Report exported successfully', 'Close', { duration: 3000 });
  }

  toggleEmployee(employee: EmployeeAttendanceSummaryDto) {
    this.expandedEmployee = this.expandedEmployee === employee ? null : employee;
  }

  isSessionIncomplete(session: AttendanceSessionDto): boolean {
    return session.clockInTime && !session.clockOutTime;
  }
} 